// Dependency-free so every rule here is unit testable without a DOM harness; the stateful half — the mutation
// recorder — lives in ./hydration-rewrite-monitor.

// Deliberately wider than the e2e harness's set: a false negative here tickets a translator's rewrite as ours.
const HYDRATION_ERROR_NUMBERS = new Set([418, 421, 422, 423, 425])

export function isHydrationError(message: string): boolean {
  const number = /minified react error #(\d+)/i.exec(message)?.[1]
  if (number) return HYDRATION_ERROR_NUMBERS.has(Number(number))
  return message.toLowerCase().includes('hydrat')
}

// Captured at module evaluation, before a translator has had reason to touch a page still being waited on.
const initialDocumentLang = typeof document !== 'undefined' ? document.documentElement.lang : undefined

// Translators rewrite text nodes in place and wrap them in <font>, which React reports as #418 text/HTML mismatches.
// Trade-off: the in-app language switcher rewrites <html lang> too, but that can only happen after hydration.
export function isDocumentTranslated(
  html: { className: string; lang: string } | undefined = typeof document !== 'undefined'
    ? document.documentElement
    : undefined,
  arrivedLang: string | undefined = initialDocumentLang,
): boolean {
  if (!html) return false
  if (/(?:^|\s)translated-(?:ltr|rtl)(?:\s|$)/.test(html.className)) return true
  return !!arrivedLang && !!html.lang && html.lang !== arrivedLang
}

/** One text node's value being swapped for another, as recorded by the hydration rewrite monitor. */
export interface TextReplacement {
  readonly before: string
  readonly after: string
}

// Long enough that nothing unrelated collides, short enough to survive the monitor truncating a value.
const MIN_WITNESS_MATCH = 24

// U+00A0 via its escape, never the literal character: a literal is invisible in a diff and one formatter pass
// from becoming an ordinary space, and `\s` would keep the test passing either way.
function normalizeText(text: string): string {
  return text
    .replace(/\u00a0/g, ' ')
    .replace(/\s+/g, ' ')
    .trim()
}

function sharesPrefix(a: string, b: string, length: number): boolean {
  if (a.length < length || b.length < length) return false
  for (let i = 0; i < length; i++) {
    if (a[i] !== b[i]) return false
  }
  return true
}

// Both ends are matched against EVERY witness: ours→ours is React repairing a genuine mismatch, and testing
// `after` against only the witness that matched `before` would classify that as foreign and silence it.
// FAILS OPEN — a missed classification costs one noisy issue, a wrong one hides the next real regression.
export function wasOurTextReplaced(replacements: readonly TextReplacement[], witnesses: readonly string[]): boolean {
  if (replacements.length === 0 || witnesses.length === 0) return false
  const normalizedWitnesses = witnesses.map(normalizeText).filter((text) => text.length >= MIN_WITNESS_MATCH)
  if (normalizedWitnesses.length === 0) return false
  const isOurs = (text: string) => normalizedWitnesses.some((witness) => sharesPrefix(text, witness, MIN_WITNESS_MATCH))
  return replacements.some((replacement) => {
    const before = normalizeText(replacement.before)
    if (before.length < MIN_WITNESS_MATCH) return false
    return isOurs(before) && !isOurs(normalizeText(replacement.after))
  })
}

/** The shortest string that can serve as a witness. Exported so the harvester applies the same floor. */
export const MIN_WITNESS_LENGTH = MIN_WITNESS_MATCH
